import { Button, Typography } from "antd";
import type React from "react";
import { useCallback } from "react";

const { Text } = Typography;

export interface TemplateSelectorButtonProps {
  /** Editor name */
  editorName?: string;
  /** Whether to show editor name */
  showEditorName?: boolean;
  /** Button text */
  buttonText?: string;
  /** Click callback */
  onClick?: (e: React.MouseEvent) => void;
  /** Whether disabled */
  disabled?: boolean;
  /** Button style class name */
  className?: string;
  /** Connection status (for collaborative editing) */
  isConnected?: boolean;
  /** Current active editor ID */
  activeEditorId?: string;
  /** Current editor ID */
  editorId?: string;
}

/**
 * Universal template selector button component
 * Used to unify template selection UI and behavior across panels
 */
const TemplateSelectorButton: React.FC<TemplateSelectorButtonProps> = ({
  editorName,
  showEditorName = true,
  buttonText,
  onClick,
  disabled = false,
  className = "",
  isConnected = false,
  activeEditorId,
  editorId,
}) => {
  // Determine if button should be disabled - moved before useCallback
  const isButtonDisabled: boolean =
    disabled || Boolean(isConnected && activeEditorId && activeEditorId !== editorId);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onClick && !isButtonDisabled) {
        onClick(e);
      }
    },
    [onClick, isButtonDisabled],
  );

  return (
    <div className="flex justify-between items-center w-full">
      {showEditorName && editorName && <Text className="text-gray-500">{editorName}</Text>}
      <Button
        type="link"
        onClick={handleClick}
        disabled={isButtonDisabled}
        className={`template-button !p-0 ${className || ""}`}
      >
        {buttonText}
      </Button>
    </div>
  );
};

export default TemplateSelectorButton;
